import {FormControl, FormGroup} from '@angular/forms';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    PaymentStatusesEnum,
    PaymentProcessesEnum,
    PaymentsService,
    PaymentReportModel,
    PaymentStatusTriggerModel
} from '@spa/api';

import {RequiredValidatorKey} from '@spa/validators';

import {Helper, RadioGroupControl} from '../../../shared/classes';
import {RadioGroupOptionInterface} from '../../../shared/components';

import {TransactionStatusesFormErrorStatesEnum} from '../enums';

import {
    PaymentStatusesControl,
    PaymentStatusControlFundingOptions,
    PaymentStatusControlWithdrawalOptions
} from '../../../../assets/descriptors';

export class PaymentStatusesForm {
    private paymentsService: PaymentsService;

    public formGroup: FormGroup;
    public control: RadioGroupControl;

    constructor(transactionsService: PaymentsService) {
        this.paymentsService = transactionsService;
    }

    private mapStatusControlOptions(
        payment: PaymentReportModel,
        triggers: number[],
        currentTrigger: number,
        options: RadioGroupOptionInterface[],
        disabledOption: boolean
    ): RadioGroupOptionInterface[] {
        return options.map(option => {
            const trigger = Helper.enumsHelper()
                .getPaymentStatusTriggerByStatus(option.value as number);

            if ((triggers.concat(currentTrigger).indexOf(trigger) < 0) || disabledOption) {
                option.disabled = true;
            }

            if (option.value === payment.paymentStatus) {
                option.checked = true;

                this.control.value = payment.paymentStatus;
            }

            return option;
        });
    }

    initForm(transaction: PaymentReportModel, paymentRequestTriggers: number[], disabledControlItems: boolean): void {
        this.formGroup = new FormGroup({});
        this.control = PaymentStatusesControl();

        const currentTrigger = Helper.enumsHelper()
            .getPaymentStatusTriggerByStatus(transaction.paymentStatus);

        let options = [];

        switch (transaction.paymentProcess) {
            case PaymentProcessesEnum.Funding:
                options = PaymentStatusControlFundingOptions();
                break;
            case PaymentProcessesEnum.Withdrawal:
                options = PaymentStatusControlWithdrawalOptions(transaction.paymentMethod);
                break;
            default:
                options = PaymentStatusControlFundingOptions();
                break;
        }

        this.control.options = this.mapStatusControlOptions(
            transaction,
            paymentRequestTriggers,
            currentTrigger,
            options,
            disabledControlItems
        );

        this.formGroup.addControl(this.control.name, new FormControl(this.control.value, this.control.validators));
    }

    refreshForm(payment: PaymentReportModel, triggers: number[], disabledControlItems: boolean): void {
        const currentTrigger = Helper.enumsHelper()
            .getPaymentStatusTriggerByStatus(payment.paymentStatus);

        this.control.options.forEach(option => {
            const trigger = Helper.enumsHelper()
                .getPaymentStatusTriggerByStatus(option.value as number);

            if ((triggers.concat(currentTrigger).indexOf(trigger) < 0) || disabledControlItems) {
                option.disabled = true;
            }
            else {
                option.disabled = false;
            }

            if (option.value === payment.paymentStatus) {
                option.checked = true;

                this.control.value = payment.paymentStatus;
            }
        });
    }

    validateForm(messagesFunction: (controlLabel?: string) => { [key: string]: string }): boolean {
        if (this.formGroup.invalid) {
            const control = this.formGroup.controls[this.control.name];

            this.control.errors = Object.keys(control.errors).map((key: string) => {
                if (key === RequiredValidatorKey) {
                    return 'Different status must be selected.';
                }

                return messagesFunction(this.control.placeholder)[key];
            });

            return false;
        }

        return true;
    }

    createRequest(
        payment: PaymentReportModel,
        updateStatusToCancelledPermissions: string[],
        authUserPermissions: string[]
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        const status = this.formGroup.value[this.control.name];

        if (
            (status === PaymentStatusesEnum.Canceled) &&
            !Helper.getUnionStrategyPermissionsCheck(updateStatusToCancelledPermissions, authUserPermissions)
        ) {
            const error = new ApiError(TransactionStatusesFormErrorStatesEnum.UpdateStatusToCancelledRejected);
            const errorResponse = new ApiResponse(HttpStatusCodesEnum.BadRequest, [error]);

            return of(errorResponse);
        }

        if (payment.paymentStatus !== status) {
            const model = new PaymentStatusTriggerModel(
                Helper.enumsHelper().getPaymentStatusTriggerByStatus(status),
                payment.paymentProcess,
                payment.paymentMethod,
                payment.paymentProvider,
                payment.paymentReference
            );

            return this.paymentsService.changePaymentStatus(model)
                .pipe(
                    map((response: ApiResponse<ApiError[] | null>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse(
                                response,
                                TransactionStatusesFormErrorStatesEnum.UpdateStatusFailed
                            );
                        }

                        switch (status) {
                            case PaymentStatusesEnum.Processed:
                                payment.paymentStatus = PaymentStatusesEnum.ForApproval;
                                break;
                            case PaymentStatusesEnum.Approved:
                                payment.paymentStatus = PaymentStatusesEnum.Completed;
                                break;
                            default:
                                payment.paymentStatus = status;
                        }

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
